import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getAttachmentBlob } from '../api/attachments';
import type { AttachmentDto, TaskDto } from '../api/types';
import {
  useAddComment,
  useAttachments,
  useComments,
  useCompleteTask,
  useCreateTag,
  useDeleteAttachment,
  useDeleteTask,
  useMe,
  useProjects,
  useTags,
  useTask,
  useUncompleteTask,
  useUpdateTask,
  useUploadAttachment
} from '../hooks/queries';
import { memberName, useWorkspace } from '../workspace/WorkspaceContext';
import {
  formatDate,
  formatDateTime,
  isoToLocalInput,
  localInputToIso,
  normalizeDueTime
} from '../utils/dates';
import { Avatar } from './Avatar';
import { RecurrenceEditor } from './RecurrenceEditor';
import { IconCheck, IconClose, IconStar, IconTrash } from './icons';

const ICE_FIELDS = [
  { key: 'iceImportance', label: 'importance' },
  { key: 'iceConfidence', label: 'confidence' },
  { key: 'iceEase', label: 'ease' }
] as const;

interface TaskDetailProps {
  taskId: string;
  onClose: () => void;
}

export function TaskDetail({ taskId, onClose }: TaskDetailProps) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const taskQuery = useTask(taskId);
  const task = taskQuery.data;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="td-backdrop" onClick={onClose} />
      <aside className="td" aria-label={task?.title}>
        {task ? (
          <TaskDetailBody
            key={task.id}
            task={task}
            onClose={onClose}
            workspaceId={workspace.id}
          />
        ) : (
          <div className="tl-loading">{t('app.loading')}</div>
        )}
      </aside>
    </>
  );
}

interface TaskDetailBodyProps {
  task: TaskDto;
  workspaceId: string;
  onClose: () => void;
}

function TaskDetailBody({ task, workspaceId, onClose }: TaskDetailBodyProps) {
  const { t } = useTranslation();
  const { members } = useWorkspace();
  const me = useMe();
  const showTags = me.data?.prefs?.useTags !== false;
  const projects = useProjects(workspaceId);
  const tags = useTags(workspaceId);
  const updateTask = useUpdateTask();
  const completeTask = useCompleteTask();
  const uncompleteTask = useUncompleteTask();
  const deleteTask = useDeleteTask();
  const createTag = useCreateTag(workspaceId);
  const comments = useComments(task.id);
  const addComment = useAddComment(task.id);
  const attachments = useAttachments(task.id);
  const uploadAttachment = useUploadAttachment(task.id);
  const deleteAttachment = useDeleteAttachment(task.id);

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [newTag, setNewTag] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [commentBody, setCommentBody] = useState('');

  useEffect(() => {
    setTitle(task.title);
    setDescription(task.description ?? '');
    setConfirmingDelete(false);
  }, [task.id]);

  const patch = (fields: Parameters<typeof updateTask.mutate>[0]['patch']) =>
    updateTask.mutate({ taskId: task.id, patch: fields });

  const commitTitle = () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setTitle(task.title);
      return;
    }
    if (trimmed !== task.title) patch({ title: trimmed });
  };

  const commitDescription = () => {
    const value = description.trim() ? description : null;
    if (value !== (task.description ?? null)) patch({ description: value });
  };

  const isCompleted = task.completedAt !== null;

  const toggleTag = (tagId: string) => {
    const next = task.tagIds.includes(tagId)
      ? task.tagIds.filter((id) => id !== tagId)
      : [...task.tagIds, tagId];
    patch({ tagIds: next });
  };

  const submitNewTag = (event: FormEvent) => {
    event.preventDefault();
    const name = newTag.trim();
    if (!name || createTag.isPending) return;
    createTag.mutate(
      { name },
      {
        onSuccess: (tag) => {
          setNewTag('');
          if (!task.tagIds.includes(tag.id)) {
            patch({ tagIds: [...task.tagIds, tag.id] });
          }
        }
      }
    );
  };

  const submitComment = (event: FormEvent) => {
    event.preventDefault();
    const body = commentBody.trim();
    if (!body || addComment.isPending) return;
    addComment.mutate(body, { onSuccess: () => setCommentBody('') });
  };

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      for (const file of Array.from(files)) {
        uploadAttachment.mutate(file);
      }
    }
    event.target.value = '';
  };

  const handleDelete = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    deleteTask.mutate(task.id, { onSuccess: onClose });
  };

  const sortedTags = [...(tags.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const sortedComments = [...(comments.data ?? [])].sort((a, b) =>
    (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
  );

  return (
    <>
      <div className="td-toolbar">
        <button
          type="button"
          className={`td-complete-btn${isCompleted ? ' done' : ''}`}
          onClick={() =>
            isCompleted ? uncompleteTask.mutate(task.id) : completeTask.mutate(task.id)
          }
        >
          <IconCheck />
          {isCompleted ? t('detail.uncomplete') : t('detail.complete')}
        </button>
        <span className="spacer" />
        <button
          type="button"
          className={`star-btn${task.starred ? ' starred' : ''}`}
          onClick={() => patch({ starred: !task.starred })}
          aria-label={task.starred ? t('tasks.unstar') : t('tasks.star')}
          aria-pressed={task.starred}
        >
          <IconStar filled={task.starred} />
        </button>
        <button
          type="button"
          className={`icon-btn${confirmingDelete ? ' confirm' : ''}`}
          onClick={handleDelete}
          style={confirmingDelete ? { color: 'var(--danger)' } : undefined}
          aria-label={t('detail.delete')}
          title={confirmingDelete ? t('detail.confirmDelete') : t('detail.delete')}
        >
          <IconTrash />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          aria-label={t('detail.close')}
        >
          <IconClose />
        </button>
      </div>

      <div className="td-scroll">
        {isCompleted && task.completedAt && (
          <div className="td-muted-note">
            {t('tasks.completedAt', { date: formatDateTime(task.completedAt) })}
          </div>
        )}
        <input
          className="td-title"
          value={title}
          placeholder={t('detail.titlePlaceholder')}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <textarea
          className="td-desc"
          value={description}
          placeholder={t('detail.descriptionPlaceholder')}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={commitDescription}
        />

        <div className="td-fields">
          <div className="td-field">
            <span className="td-field-label">{t('detail.project')}</span>
            <div className="td-field-control">
              <select
                className="td-select"
                value={task.projectId ?? ''}
                onChange={(e) =>
                  patch({ projectId: e.target.value || null, sectionId: null })
                }
              >
                <option value="">{t('detail.inbox')}</option>
                {(projects.data ?? [])
                  .filter((project) => !project.archived || project.id === task.projectId)
                  .map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="td-field">
            <span className="td-field-label">{t('detail.assignee')}</span>
            <div className="td-field-control">
              <select
                className="td-select"
                value={task.assigneeUserId ?? ''}
                onChange={(e) => patch({ assigneeUserId: e.target.value || null })}
              >
                <option value="">{t('detail.unassigned')}</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.displayName || member.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="td-field">
            <span className="td-field-label">{t('detail.due')}</span>
            <div className="td-field-control">
              <input
                type="date"
                className="td-date"
                value={task.dueDate ?? ''}
                onChange={(e) =>
                  patch(
                    e.target.value
                      ? { dueDate: e.target.value }
                      : { dueDate: null, dueTime: null }
                  )
                }
              />
              {task.dueDate && (
                <>
                  <input
                    type="time"
                    className="td-date"
                    value={task.dueTime ? task.dueTime.slice(0, 5) : ''}
                    onChange={(e) => patch({ dueTime: normalizeDueTime(e.target.value) })}
                  />
                  <button
                    type="button"
                    className="td-clear"
                    onClick={() => patch({ dueDate: null, dueTime: null })}
                    aria-label={t('detail.clear')}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="td-field">
            <span className="td-field-label" title={t('ice.label')}>
              {t('ice.short')}
            </span>
            <div className="td-field-control td-ice">
              {ICE_FIELDS.map(({ key, label }) => (
                <label key={key} className="td-ice-part" title={t(`ice.${label}`)}>
                  <span className="td-ice-letter">{t(`ice.${label}Short`)}</span>
                  <select
                    className="td-select"
                    aria-label={t(`ice.${label}`)}
                    value={task[key] ?? ''}
                    onChange={(e) => patch({ [key]: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">-</option>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              <span className="td-ice-score">
                {task.iceScore !== null ? `= ${task.iceScore}` : ''}
              </span>
            </div>
          </div>

          <div className="td-field">
            <span className="td-field-label">{t('detail.recurrence')}</span>
            <RecurrenceEditor
              value={task.recurrence}
              onChange={(recurrence) => patch({ recurrence })}
            />
          </div>

          <div className="td-field">
            <span className="td-field-label">{t('detail.reminder')}</span>
            <div className="td-field-control">
              <input
                type="datetime-local"
                className="td-date"
                value={isoToLocalInput(task.reminderAt)}
                onChange={(e) => patch({ reminderAt: localInputToIso(e.target.value) })}
              />
              {task.reminderAt && (
                <button
                  type="button"
                  className="td-clear"
                  onClick={() => patch({ reminderAt: null })}
                  aria-label={t('detail.clear')}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {showTags && (
          <div className="td-field">
            <span className="td-field-label">{t('detail.tags')}</span>
            <div className="td-tags">
              {sortedTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className={`td-tag-toggle${task.tagIds.includes(tag.id) ? ' on' : ''}`}
                  onClick={() => toggleTag(tag.id)}
                >
                  <span className="color-dot" style={{ background: tag.color }} />
                  {tag.name}
                </button>
              ))}
              <form className="td-tag-new" onSubmit={submitNewTag}>
                <input
                  value={newTag}
                  placeholder={t('detail.addTag')}
                  onChange={(e) => setNewTag(e.target.value)}
                />
              </form>
            </div>
          </div>
          )}
        </div>

        <div className="td-attachments">
          <div className="td-comments-head">{t('detail.attachments')}</div>
          {(attachments.data ?? []).length === 0 && (
            <div className="modal-hint">{t('detail.noAttachments')}</div>
          )}
          {(attachments.data ?? []).length > 0 && (
            <div className="td-attachment-grid">
              {(attachments.data ?? []).map((attachment) => (
                <div key={attachment.id} className="td-attachment">
                  {attachment.contentType.startsWith('image/') &&
                    attachment.uploadState === 'UPLOADED' && (
                      <AttachmentThumb attachment={attachment} />
                    )}
                  <button
                    type="button"
                    className="td-attachment-name"
                    title={attachment.filename}
                    disabled={attachment.uploadState === 'PENDING'}
                    onClick={() => openAttachment(attachment.id)}
                  >
                    {attachment.filename}
                  </button>
                  <div className="td-attachment-meta">
                    {attachment.uploadState === 'PENDING'
                      ? t('detail.pendingUpload')
                      : formatBytes(attachment.sizeBytes)}
                  </div>
                  <button
                    type="button"
                    className="icon-btn td-attachment-delete"
                    onClick={() => deleteAttachment.mutate(attachment.id)}
                    aria-label={t('detail.deleteAttachment')}
                    title={t('detail.deleteAttachment')}
                  >
                    <IconTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
          {uploadAttachment.isPending && (
            <div className="modal-hint">{t('detail.uploading')}</div>
          )}
          <label className="btn td-attachment-add">
            {t('detail.addAttachment')}
            <input type="file" multiple hidden onChange={handleFilesSelected} />
          </label>
        </div>

        <div className="td-comments">
          <div className="td-comments-head">{t('detail.comments')}</div>
          {sortedComments.length === 0 && (
            <div className="modal-hint">{t('detail.noComments')}</div>
          )}
          {sortedComments.map((comment) => {
            const author = memberName(members, comment.authorUserId);
            return (
              <div key={comment.id} className="td-comment">
                <Avatar name={author || '?'} />
                <div className="td-comment-body">
                  <div className="td-comment-meta">
                    <strong>{author}</strong>
                    <time>{formatDateTime(comment.createdAt)}</time>
                  </div>
                  <div className="td-comment-text">{comment.body}</div>
                </div>
              </div>
            );
          })}
          <form className="td-comment-form" onSubmit={submitComment}>
            <input
              className="field-input"
              value={commentBody}
              placeholder={t('detail.commentPlaceholder')}
              onChange={(e) => setCommentBody(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!commentBody.trim() || addComment.isPending}
            >
              {t('detail.send')}
            </button>
          </form>
        </div>
      </div>

      <div className="td-footer">
        {t('detail.createdBy', {
          name: memberName(members, task.createdBy),
          date: formatDate(task.createdAt)
        })}
      </div>
    </>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function openAttachment(attachmentId: string): void {
  void getAttachmentBlob(attachmentId)
    .then((blob) => {
      window.open(URL.createObjectURL(blob));
    })
    .catch(() => undefined);
}

interface AttachmentThumbProps {
  attachment: AttachmentDto;
}

function AttachmentThumb({ attachment }: AttachmentThumbProps) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    getAttachmentBlob(attachment.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);

  if (!url) return null;

  return (
    <img
      className="td-attachment-thumb"
      src={url}
      alt={attachment.filename}
      onClick={() => window.open(url)}
    />
  );
}
